import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { AuthCard } from '@/components/auth-card'
import { Button } from '@/components/ui/button'
import { authClient } from '@/lib/auth-client'

export const Route = createFileRoute('/invite/$invitationId')({
  component: AcceptInvitationPage,
})

function AcceptInvitationPage() {
  const { invitationId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  async function accept() {
    setPending(true)
    setError(null)
    const { error: acceptError } = await authClient.organization.acceptInvitation({ invitationId })
    setPending(false)

    if (acceptError) {
      // The usual cause is not being signed in as the invited address.
      setError(acceptError.message ?? 'Could not accept this invitation')
      return
    }

    await queryClient.invalidateQueries()
    navigate({ to: '/' })
  }

  return (
    <AuthCard
      title="Join workspace"
      description="You have been invited to collaborate on a workspace."
      footer={
        <>
          Signed in as the wrong account?{' '}
          <button
            type="button"
            className="text-link underline-offset-4 hover:underline"
            onClick={async () => {
              await authClient.signOut()
              queryClient.clear()
              navigate({ to: '/login', search: { redirect: window.location.pathname } })
            }}
          >
            Switch account
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <p className="text-[12px] text-destructive">{error}</p>}
        <Button className="w-full" onClick={accept} disabled={pending}>
          {pending ? 'Joining…' : 'Accept invitation'}
        </Button>
      </div>
    </AuthCard>
  )
}
